import { useState, useRef, useEffect } from 'react';
import {
  Menu, X, FlaskConical, Save, FolderOpen, Download, Upload,
  Settings, Bot, Layers, Star, Variable, Eye, Code, Minimize2
} from 'lucide-react';
import SettingsPanel from './SettingsPanel';
import ThemeSwitcher from './ThemeSwitcher';

const TopBar = ({
  onTestFlow,
  onSave,
  onLoad,
  onShowTemplates,
  onShowExportImport,
  onToggleLLMAssist,
  onToggleHeatmap,
  onShowVariables,
  onTogglePreview,
  onShowEmbedCode,
  isTesting,
  showLLMAssist,
  showHeatmap,
  // showPreviewPlayer unused in render but kept in API for caller compatibility
  currentFlow,
  availableFlows,
  onFlowChange,
  view,
  onViewChange,
  compact75,
  onToggleCompact,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const item = (Icon, label, onClick, active = false, danger = false) => (
    <button
      onClick={() => { onClick(); setMenuOpen(false); }}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 16px',
        fontSize: '0.7rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontFamily: 'inherit',
        textAlign: 'left',
        background: active
          ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
          : 'transparent',
        color: danger
          ? '#ff4e7a'
          : active
            ? 'var(--accent)'
            : 'var(--text)',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 0.15s, color 0.15s',
      }}
      onMouseEnter={e => {
        if (!active) {
          e.currentTarget.style.background = danger
            ? 'color-mix(in srgb, #ff4e7a 10%, transparent)'
            : 'color-mix(in srgb, var(--accent) 8%, transparent)';
          e.currentTarget.style.color = danger ? '#ff4e7a' : 'var(--accent)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = active
          ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
          : 'transparent';
        e.currentTarget.style.color = danger
          ? '#ff4e7a'
          : active
            ? 'var(--accent)'
            : 'var(--text)';
      }}
    >
      <Icon size={13} style={{ flexShrink: 0, opacity: 0.85 }} />
      {label}
      {active && (
        <span style={{
          marginLeft: 'auto',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--accent)',
          boxShadow: '0 0 6px var(--accent)',
          flexShrink: 0,
        }} />
      )}
    </button>
  );

  const divider = () => <hr className="af-divider" style={{ margin: '4px 0' }} />;

  return (
    <div
      style={{
        height: 44,
        background: 'var(--panel)',
        borderBottom: '1px solid color-mix(in srgb, var(--line) 25%, transparent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        gap: 8,
        flexShrink: 0,
        fontFamily: 'inherit',
      }}
    >
      {/* Left: Brand + Flow Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {/* Brand */}
        <span
          className="af-text-accent"
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.15em',
            whiteSpace: 'nowrap',
          }}
        >
          [ AF ]
        </span>

        {/* Vertical separator */}
        <span style={{ width: 1, height: 18, background: 'color-mix(in srgb, var(--line) 30%, transparent)', flexShrink: 0 }} />

        {/* Flow name — editable in builder mode */}
        {view === 'builder' ? (
          <input
            value={currentFlow || ''}
            onChange={e => onFlowChange?.(e.target.value)}
            placeholder="Untitled Flow"
            style={{
              fontSize: '0.65rem',
              letterSpacing: '0.06em',
              background: 'color-mix(in srgb, var(--line) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--line) 35%, transparent)',
              color: 'var(--text)',
              padding: '3px 7px',
              borderRadius: 2,
              fontFamily: 'inherit',
              minWidth: 80,
              maxWidth: 180,
              outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent)'}
            onBlur={e => e.target.style.borderColor = 'color-mix(in srgb, var(--line) 35%, transparent)'}
          />
        ) : (
          <span style={{ fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            Visual AI Workflow
          </span>
        )}
      </div>

      {/* Center: View Toggle */}
      {onViewChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          <button
            onClick={() => onViewChange('dashboard')}
            className={view === 'dashboard' ? 'af-btn af-btn-primary' : 'af-btn af-btn-ghost'}
            style={{ padding: '4px 12px', fontSize: '0.62rem', letterSpacing: '0.12em' }}
          >
            Dashboard
          </button>
          <button
            onClick={() => onViewChange('builder')}
            className={view === 'builder' ? 'af-btn af-btn-primary' : 'af-btn af-btn-ghost'}
            style={{ padding: '4px 12px', fontSize: '0.62rem', letterSpacing: '0.12em' }}
          >
            Builder
          </button>
        </div>
      )}

      {/* Right: ThemeSwitcher + Hamburger */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <ThemeSwitcher compact />

        <span style={{ width: 1, height: 18, background: 'color-mix(in srgb, var(--line) 30%, transparent)' }} />

        {/* Hamburger */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="af-btn"
            style={{
              padding: '4px 10px',
              fontSize: '0.62rem',
              letterSpacing: '0.1em',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: menuOpen
                ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
                : undefined,
              borderColor: menuOpen ? 'var(--accent)' : undefined,
              color: menuOpen ? 'var(--accent)' : undefined,
            }}
            aria-label="Open menu"
          >
            {menuOpen ? <X size={14} /> : <Menu size={14} />}
            <span style={{ display: 'inline' }}>Menu</span>
          </button>

          {menuOpen && (
            <div
              className="af-panel"
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 6px)',
                width: 220,
                zIndex: 9999,
                overflow: 'hidden',
                padding: '6px 0',
              }}
            >
              {/* Test Flow */}
              <button
                onClick={() => { onTestFlow?.(); setMenuOpen(false); }}
                disabled={isTesting}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 16px',
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  background: 'transparent',
                  color: isTesting ? 'var(--muted)' : 'var(--accent)',
                  border: 'none',
                  cursor: isTesting ? 'not-allowed' : 'pointer',
                  opacity: isTesting ? 0.5 : 1,
                  fontWeight: 600,
                }}
                onMouseEnter={e => {
                  if (!isTesting) e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 10%, transparent)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <FlaskConical size={13} style={{ flexShrink: 0 }} />
                {isTesting ? 'Testing...' : 'Test Flow'}
              </button>

              {divider()}

              {item(Save,       'Save',     () => onSave?.())}
              {item(FolderOpen, 'Load',     () => onLoad?.())}
              {item(Download,   'Export',   () => onShowExportImport?.())}
              {item(Upload,     'Import',   () => onShowExportImport?.())}

              {divider()}

              {item(Star,     'Templates',         () => onShowTemplates?.())}
              {item(Layers,   'Performance Heatmap', () => onToggleHeatmap?.(),  showHeatmap)}
              {item(Bot,      'AI Assist',          () => onToggleLLMAssist?.(), showLLMAssist)}
              {item(Variable, 'Variables',          () => onShowVariables?.())}

              {divider()}

              {item(Eye,  'Preview',    () => onTogglePreview?.())}
              {item(Code, 'Embed Code', () => onShowEmbedCode?.())}

              {divider()}

              {item(Settings,  'Settings',                      () => { setShowSettings(true); })}
              {item(Minimize2, compact75 ? 'Compact: On' : 'Compact', () => onToggleCompact?.(), compact75)}
            </div>
          )}
        </div>
      </div>

      {showSettings && (
        <SettingsPanel onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

export default TopBar;
